const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class DarkrimMilestones extends HTMLElement {
  #activeIndex = -1;
  #items = [];
  #panels = [];
  #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  #timeline = null;
  #triggers = [];

  connectedCallback() {
    this.#timeline = this.querySelector('[data-darkrim-miles-timeline]');
    this.#items = Array.from(this.querySelectorAll('[data-darkrim-miles-item]'));
    this.#triggers = Array.from(this.querySelectorAll('[data-darkrim-miles-trigger]'));
    this.#panels = Array.from(this.querySelectorAll('[data-darkrim-miles-panel]'));

    if (this.#triggers.length === 0) return;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    window.addEventListener('resize', this.#handleResize);

    this.#setActiveIndex(Number(this.dataset.defaultIndex), { immediate: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    window.removeEventListener('resize', this.#handleResize);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-darkrim-miles-trigger]');
    if (trigger && this.contains(trigger)) {
      this.#setActiveIndex(Number(trigger.dataset.index));
      return;
    }

    if (event.target.closest('[data-darkrim-miles-prev]')) {
      this.#setActiveIndex(this.#activeIndex - 1);
      return;
    }

    if (event.target.closest('[data-darkrim-miles-next]')) {
      this.#setActiveIndex(this.#activeIndex + 1);
    }
  };

  #handleKeydown = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-darkrim-miles-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    const currentIndex = Number(trigger.dataset.index);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = currentIndex + 1;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = currentIndex - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.#triggers.length - 1;
    if (nextIndex === currentIndex) return;

    event.preventDefault();
    this.#setActiveIndex(nextIndex, { focus: true });
  };

  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[data-darkrim-miles-item]');
    if (!item || !this.contains(item)) return;

    this.#setActiveIndex(Number(item.dataset.index), { immediate: true });
  };

  #handleResize = () => {
    this.#centerTimeline(true);
  };

  #setActiveIndex(index, options = {}) {
    const nextIndex = this.#normalizeIndex(index);
    if (nextIndex === this.#activeIndex && !options.immediate && !options.focus) return;

    this.#activeIndex = nextIndex;

    this.#triggers.forEach((trigger, triggerIndex) => {
      const selected = triggerIndex === nextIndex;
      trigger.setAttribute('aria-selected', String(selected));
      trigger.tabIndex = selected ? 0 : -1;
      trigger.closest('[data-darkrim-miles-item]')?.classList.toggle('is-active', selected);
      if (selected && options.focus) trigger.focus();
    });

    this.#panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== nextIndex;
    });

    this.#centerTimeline(Boolean(options.immediate));
  }

  #centerTimeline(immediate) {
    const item = this.#items[this.#activeIndex];
    if (!this.#timeline || !item) return;
    if (this.#timeline.scrollWidth <= this.#timeline.clientWidth + 1) return;

    const left = item.offsetLeft + item.offsetWidth / 2 - this.#timeline.clientWidth / 2;
    const behavior = immediate || this.#reducedMotionQuery.matches ? 'auto' : 'smooth';
    this.#timeline.scrollTo({ left: Math.max(0, left), behavior });
  }

  #normalizeIndex(index) {
    const count = this.#triggers.length;
    if (count === 0) return 0;

    const numericIndex = Number.isFinite(Number(index)) ? Number(index) : 0;
    return ((numericIndex % count) + count) % count;
  }
}

if (!customElements.get('darkrim-milestones')) {
  customElements.define('darkrim-milestones', DarkrimMilestones);
}
